<template>
  <div class="w-full flex-none self-stretch grow-0">
    <div
      v-if="!isLoadingFetch && !devices.length"
      class="flex flex-col items-start p-2 gap-4 w-full bg-[#EEF2F4] rounded-md box-border"
    >
      <span class="font-sans font-normal text-sm leading-[17px] text-[#717379]">
        {{ $t('device.choose_device_title') }}
      </span>
      <p class="self-stretch flex-none grow-0  text-sm leading-[17px] text-center text-[#2D3032] m-0">
        {{ $t('home.no_devices') }}
      </p>
      <Button
        :label="$t('device.connect_device')"
        icon="pi pi-plus"
        severity="primary"
        size="small"
        class="w-full"
        @click.stop="showAddDeviceModal = true"
      />
    </div>

    <div
      v-else
      class="flex flex-col items-start p-2 gap-2.5 w-full bg-[#eef2f4] rounded-md flex-none self-stretch grow-0 overflow-y-auto box-border"
    >
      <div class="flex items-center justify-between w-full">
        <span class="w-[90px] h-[17px] font-sans font-normal text-sm leading-[17px] text-[#717379] flex-none order-0 grow-0">
          {{ $t('device.choose_device_title') }}
        </span>

        <Button
          v-tooltip.bottom="{ value: $t('home.add_device'), disabled: true }"
          type="button"
          variant="outlined"
          severity="primary"
          size="small"
          class="w-8 h-8 rounded-full btn-ghost"
          icon="pi pi-plus"
          :aria-label="$t('home.add_device')"
          @click.stop="showAddDeviceModal = true"
        />
      </div>

      <div
        v-if="isLoadingFetch"
        class="flex flex-col gap-2 w-full"
      >
        <Skeleton
          v-for="i in 3"
          :key="i"
          height="2.5rem"
        />
      </div>

      <template v-else>
        <div class="flex flex-col gap-2 w-full">
          <div
            v-for="deviceItem in visibleDevices"
            :key="deviceItem.id || deviceItem.name"
            class="flex flex-row items-center gap-2 w-full p-2 bg-white rounded-md"
          >
            <div
              v-if="getDeviceIcon(deviceItem.type)"
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#c6d3db] p-1.5"
            >
              <img
                :src="getDeviceIcon(deviceItem.type)"
                :alt="deviceItem.name"
                :title="deviceItem.type"
                class="max-h-full max-w-full"
              >
            </div>
            <div class="flex flex-col items-start flex-1 min-w-0">
              <span class="font-bold text-sm leading-[17px] text-[#2d3032] overflow-hidden text-ellipsis whitespace-nowrap w-full">{{ deviceItem.name }}</span>
              <span
                class="flex items-center text-[13px] capitalize"
                :class="statusTextClass(deviceItem.status)"
              >
                <span
                  class="inline-block w-2 h-2 rounded-full mr-[5px]"
                  :class="statusDotClass(deviceItem.status)"
                />
                {{ $t('device.' + deviceItem.status) }}
              </span>
            </div>
            <div class="flex items-center gap-2 ml-auto shrink-0">
              <Button
                v-tooltip.top="$t('common.open_device')"
                icon="pi pi-window-maximize"
                severity="secondary"
                rounded
                text
                :aria-label="$t('common.open_device')"
                @click="goToDevice(deviceItem)"
              />
              <Divider layout="vertical" />
              <Button
                v-tooltip.top="$t('common.edit')"
                icon="pi pi-cog"
                severity="secondary"
                rounded
                text
                :aria-label="$t('common.edit')"
                @click="goToEditDevice(deviceItem)"
              />
            </div>
          </div>
        </div>

        <button
          v-if="devices.length > MAX_VISIBLE_DEVICES"
          type="button"
          class="self-start flex items-center gap-1.5 bg-transparent border-none text-[#7190a6] text-[13px] font-bold cursor-pointer p-2"
          @click="showAll"
        >
          <i class="pi pi-chevron-right" />
          {{ $t('filter.show_all') }}
        </button>
      </template>
    </div>

    <div class="add-device--modal-only">
      <AddDeviceComponent
        :show-modal="showAddDeviceModal"
        @device-added="onDeviceAdded"
        @close-modal="showAddDeviceModal = false"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import Skeleton from 'primevue/skeleton';
import Button from 'primevue/button';
import Divider from 'primevue/divider';
import Tooltip from 'primevue/tooltip';
import { useDeviceManagement } from '@/composables/useDeviceManagement';
import AddDeviceComponent from '@/components/home/AddDeviceComponent.vue';
import type { Device } from '@/types/device';

const MAX_VISIBLE_DEVICES = 3;

export default defineComponent({
  name: 'ProfileMenuDevicesSection',
  components: {
    Skeleton,
    Button,
    Divider,
    AddDeviceComponent,
  },
  directives: {
    tooltip: Tooltip,
  },
  emits: ['close-menu'],
  setup(_props, { emit }) {
    const { devices, isLoadingFetch, addDeviceToList } = useDeviceManagement();
    const router = useRouter();
    const toast = useToast();
    const { t } = useI18n();

    const visibleDevices = computed(() => devices.value.slice(0, MAX_VISIBLE_DEVICES));
    const showAddDeviceModal = ref(false);

    const statusDotClass = (status: string) => {
      switch (status) {
        case 'connected':
          return 'bg-[#598836]';
        case 'pending':
          return 'border border-[#D8A200]';
        default:
          return 'border border-[#8A3F55]';
      }
    };

    const statusTextClass = (status: string) => {
      switch (status) {
        case 'connected':
          return 'text-[#598836]';
        case 'pending':
          return 'text-[#D8A200]';
        default:
          return 'text-[#8A3F55]';
      }
    };

    const getDeviceIcon = (type?: string): string => {
      if (!type) return '';
      if (type.toLowerCase() === 'enbas') {
        return new URL('@/assets/icons/home_energy.svg', import.meta.url).href;
      } else if (type.toLowerCase() === 'lobas') {
        return new URL('@/assets/icons/car_energy.svg', import.meta.url).href;
      }
      return '';
    };

    const goToDevice = (deviceItem: Device) => {
      if (deviceItem.status !== 'connected') {
        toast.warning(t('device.currently_unavailable'));
        return;
      }
      emit('close-menu');
      router.push({ name: 'device', params: { deviceId: deviceItem.id } });
    };

    const goToEditDevice = (deviceItem: Device) => {
      emit('close-menu');
      router.push({
        name: 'edit-device',
        params: {
          id: deviceItem.id,
        },
      });
    };

    const showAll = () => {
      emit('close-menu');
      router.push({ name: 'home' });
    };

    const onDeviceAdded = (newDevice: Device) => {
      addDeviceToList(newDevice);
    };

    return {
      MAX_VISIBLE_DEVICES,
      devices,
      visibleDevices,
      isLoadingFetch,
      statusDotClass,
      statusTextClass,
      getDeviceIcon,
      goToDevice,
      goToEditDevice,
      showAll,
      showAddDeviceModal,
      onDeviceAdded,
    };
  },
});
</script>

<style scoped>
.add-device--modal-only {
  display: none;
}
</style>
